import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuProps,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { useLanguage } from '@/modules/common/application/context/language';
import { useUser } from '@/modules/common/application/context/user';
import { DEFAULT_NEW_APP_ROUTE } from '@/modules/common/application/route-fallback';
import useLogout from '@/modules/common/application/use-logout';
import useSidebarNavigation from '@/modules/common/application/use-sidebar-navigation';
import { getListItemStyles } from '@/modules/common/components/Base/Drawer/styles';
import { FeatureFlag, LanguageCode } from '@/modules/common/types';

interface Props {
  drawerExpanded: boolean;
}

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    width: '54px',
    boxShadow: theme.custom.boxShadow,
    border: theme.custom.border,
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
  },
}));

const DrawerBottom = ({ drawerExpanded }: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasCompleteProfile, hasCompanyOnboarded, company } = useUser();
  const { setLanguage } = useLanguage();
  const { getBottomNavigation } = useSidebarNavigation();
  const { logout } = useLogout();
  const location = useLocation();
  const isMavue2Enabled = useFeatureFlagEnabled(FeatureFlag.MAVUE_2);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClickLanguageSwich = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSwitch = (lang: string) => {
    if (setLanguage !== undefined)
      setLanguage(lang as LanguageCode)?.then(() => {
        setAnchorEl(null);
      });
  };

  const handleClickSettings = () => navigate('/settings');
  const handleClickLogout = async () => await logout(company?.tenantId ?? undefined);
  const handleClickMavue2 = () => navigate(DEFAULT_NEW_APP_ROUTE);

  const menuItems = getBottomNavigation([
    handleClickSettings,
    handleClickLanguageSwich,
    handleClickLogout,
    handleClickMavue2,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        marginTop: 'auto',
        flexDirection: 'column',
        marginBottom: '32px',
      }}
    >
      <List>
        {menuItems.map((item) => {
          const { Icon, label, handleClick, dataCy } = item;
          if (
            (!hasCompleteProfile || !hasCompanyOnboarded) &&
            dataCy !== 'action-logout' &&
            dataCy !== 'action-language'
          )
            return null;

          if (hasCompleteProfile && hasCompanyOnboarded && dataCy === 'action-language')
            return null;

          if (!isMavue2Enabled && dataCy === 'action-mavue2') return null;

          const isActiveSettings =
            (location.pathname === '/settings' || location.pathname.startsWith('/settings/')) &&
            label.toLowerCase().includes('settings');
          return (
            <ListItem
              key={dataCy}
              sx={{
                display: 'flex',
                py: '4px',
              }}
            >
              <ListItemButton
                sx={getListItemStyles(theme, drawerExpanded)}
                selected={isActiveSettings}
                onClick={handleClick}
                data-cy={dataCy}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  <Icon className="ml-3 mr-2 h-8 w-8" />
                </ListItemIcon>
                <ListItemText disableTypography sx={{ opacity: drawerExpanded ? 1 : 0 }}>
                  {t(label)}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {(!hasCompleteProfile || !hasCompanyOnboarded) && (
        <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={() => handleLanguageSwitch('en')} disableRipple>
            EN
          </MenuItem>
          <MenuItem onClick={() => handleLanguageSwitch('de')} disableRipple>
            DE
          </MenuItem>
        </StyledMenu>
      )}
    </Box>
  );
};

export default DrawerBottom;
